import React, { useContext, useEffect, useState } from 'react'
import { CSVLink } from 'react-csv'
import { toast } from 'react-toastify'
import { Cancel, CheckCircle, Delete } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import DownloadIcon from 'assets/icons/circles-design/download_icon.png'
import PlusIcon from 'assets/icons/circles-design/plus.png'

import AdminAPI from '../../../api/orbitV1/AdminAPI'
import BreadcrumbsMUI from '../../../common/AbstractBreadcrumbs/BreadcrumbsMUI'
import MUIDataGrid from '../../../common/AbstractDataGrid/DataGridMUI'
import PageLayout from '../../../common/Layout/Page/PageLayout'
import { ColorBoxContent } from '../../../common/ui/colorBox'
import { ERR_UNEXPECTED } from '../../../constants/errors'
import JoyrideContext from '../../../context/joyride/joyrideContext'

export default function BatchUpdate() {
  const joyrideContext = useContext(JoyrideContext)
  const { tourActive, setRun } = joyrideContext

  const [error, setError] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [dialogMsg, setDialogMsg] = useState('')
  const [uploadedBatch, setUploadedBatch] = useState(null)

  const [activeStep, setActiveStep] = useState(0)
  const [parsedData, setParsedData] = useState([])

  const [selection, setSelection] = useState([])

  const handleNextStep = () => setActiveStep((prev) => prev + 1)
  const handlePrevStep = () => setActiveStep((prev) => prev - 1)
  // const handleReset = () => setActiveStep(0)

  /* template in csv format */

  const templateName = 'batch-update-template.csv'
  const headers = [
    { label: 'Name', key: 'name' },
    { label: 'Mobile Number', key: 'msisdn' }
  ]
  const customerData = [
    { name: 'John Doe', msisdn: '11111111' },
    { name: 'Raed Tree', msisdn: '22222222' }
  ]

  const isProperName = (params) => {
    return /.*\S.*/.test(params)
  }

  const isProperMsisdn = (params) => {
    return /([689])\d{7}/g.test(Number(params))
  }

  const handleCSVParse = () => {
    if (!uploadedBatch) {
      return
    }

    const parseCsv = (str) => {
      const rowArray = str.split('\n')

      const combinedHeaderString = headers
        .map((hdrObj) => {
          return `"${hdrObj.key}"`
        })
        .join(',')

      // check if the headers are correct
      if (rowArray[0] !== combinedHeaderString) {
        return toast.error('Incorrect headers')
      }

      const parsedArray = []

      const headerStrippedArray = rowArray.slice(1)
      headerStrippedArray.forEach((rowStr, index) => {
        const cleanedRowArr = rowStr.replaceAll('"', '').split(',')
        const parsedRowObj = {
          id: index,
          name: cleanedRowArr[0],
          msisdn: cleanedRowArr[1]
        }
        parsedArray.push(parsedRowObj)
      })

      return parsedArray
    }

    setError(false)
    const reader = new FileReader()

    reader.onload = async (e) => {
      const text = e.target.result
      const parsedText = parseCsv(text)

      setParsedData(parsedText)
      setSelection(
        parsedText.map((r) => {
          if (isProperMsisdn(r.msisdn) && isProperName(r.name)) {
            return r.id
          } else {
            return null
          }
        })
      )
    }
    reader.readAsText(uploadedBatch)
  }

  const handleBatchSubmit = async () => {
    // implement filter and test for valid data
    const batchSubmitArray = parsedData.map((row) => {
      return [row.name, row.msisdn]
    })

    try {
      const createdTagsResponse = await AdminAPI.createMsisdnTags({
        msisdnTags: batchSubmitArray
      })

      if (createdTagsResponse.data) {
        setDialogMsg(createdTagsResponse.data.message)
      }
      setIsDialogOpen(true)
    } catch (err) {
      setError(true)
    }
  }

  const DownloadTemplate = () => {
    return (
      <div id='batchUpdate__downloadTemplateBtn'>
        <CSVLink
          data={customerData}
          filename={templateName}
          header={headers}
          style={{ textDecoration: 'flex' }}
        >
          <Box display='flex' alignItems='center'>
            <Typography
              sx={{
                color: '#0000FF',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              Download Template
            </Typography>
            <img
              src={DownloadIcon}
              alt='download-template-logo'
              width='20px'
              height='20px'
            />
          </Box>
        </CSVLink>
      </div>
    )
  }

  const ParseFileContents = () => {
    return (
      <>
        <div id='batchUpdate__uploadFileArea'>
          <Typography>Upload File:</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%',
              border: '1px dashed #B8C4D3',
              borderRadius: '8px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                margin: '50px 100px'
              }}
            >
              <label htmlFor='uploadNumbers' style={{ cursor: 'pointer' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img src={PlusIcon} alt='plus-icon' width='20px' />
                  <Typography
                    variant='h6'
                    sx={{
                      color: '#0000FF',
                      marginLeft: 8
                    }}
                  >
                    Select File
                  </Typography>
                </Box>
              </label>
              <input
                id='uploadNumbers'
                type='file'
                accept='.csv'
                onChange={(e) => setUploadedBatch(e.target.files[0])}
                hidden
              />
            </Box>
          </Box>
        </div>
        {uploadedBatch && (
          <Box display='flex'>
            <Typography>Uploaded File: </Typography>
            <Typography variant='h6'>{uploadedBatch.name}</Typography>
          </Box>
        )}
        <Typography variant='subtitle1' sx={{ color: '#B8C4D3' }}>
          xlsx / csv, max 8mb
        </Typography>
      </>
    )
  }

  const removeRow = (id) => {
    setParsedData((current) => {
      return [...current.slice(0, id), ...current.slice(id + 1)]
    })
  }

  const addNewRow = () => {
    setParsedData((current) => {
      return [
        ...current,
        {
          id: current.length,
          name: '',
          msisdn: ''
        }
      ]
    })
  }

  const CSVChecker = () => {
    const columns = [
      {
        field: 'name',
        headerName: 'Name',
        width: 150,
        editable: true,
        valueSetter: (params) => {
          setParsedData(
            parsedData.map((data, index) => {
              if (index === params.row.id) {
                return { ...data, name: params.value }
              } else {
                return { ...data }
              }
            })
          )
        },
        cellClassName: (params) => {
          if (!isProperName(params.value)) {
            return 'batch-update-name-error'
          } else {
            return 'batch-update-ok'
          }
        }
      },
      {
        field: 'msisdn',
        headerName: 'Phone Number',
        width: 150,
        editable: true,
        valueSetter: (params) => {
          setParsedData(
            parsedData.map((data, index) => {
              if (index === params.row.id) {
                return { ...data, msisdn: params.value }
              } else {
                return { ...data }
              }
            })
          )
        },
        cellClassName: (params) => {
          if (!isProperMsisdn(params.value)) {
            return 'batch-update-msisdn-error'
          } else {
            return 'batch-update-ok'
          }
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        renderCell: (params) => {
          if (
            isProperName(params.row.name) &&
            isProperMsisdn(params.row.msisdn)
          ) {
            return <CheckCircle />
          } else {
            return <Cancel />
          }
        }
      },
      {
        field: 'action',
        headerName: 'Remove',
        renderCell: (params) => {
          return (
            <Button
              onClick={() => {
                removeRow(params.row.id)
              }}
            >
              <Delete />
            </Button>
          )
        }
      }
    ]

    return (
      <>
        <Button onClick={addNewRow}> + </Button>
        <Box
          sx={{
            '& .batch-update-msisdn-error': {
              backgroundColor: '#d47483'
            },
            '& .batch-update-name-error': {
              backgroundColor: '#d47483'
            },
            '& .batch-update-ok': {
              backgroundColor: 'rgba(157, 255, 118, 0.49)'
            }
          }}
        >
          <MUIDataGrid
            rows={parsedData}
            getRowId={(row) => row.id}
            columns={columns}
            checkboxSelection
            isRowSelectable={(params) =>
              isProperMsisdn(params.row.msisdn) && isProperName(params.row.name)
            }
            onSelectionModelChange={(newSelectionModel) => {
              setSelection(newSelectionModel)
            }}
            selectionModel={selection}
          />
        </Box>
      </>
    )
  }

  const steps = [
    {
      label: 'Download Template',
      description:
        'Download this template to assign individual names to the mobile numbers.',
      component: <DownloadTemplate />,
      handleNextStep
    },
    {
      label: 'Parse File Contents',
      description:
        'Assign the names under Column 1 according to the mobile numbers in Column 2. <br /> Save and upload the file below.',
      component: <ParseFileContents />,
      handleNextStep: () => {
        handleCSVParse()
        handleNextStep()
      }
    },
    {
      label: 'Check contents',
      description: 'Please check',
      component: <CSVChecker />,
      handleNextStep: () => {
        handleBatchSubmit()
        handleNextStep()
      }
    }
  ]

  useEffect(() => {
    if (tourActive) {
      setTimeout(() => {
        setRun(true)
      }, 500)
    }
  }, [])

  return (
    <PageLayout header='Batch Update'>
      <Grid container direction='column' spacing={2}>
        <Grid item>
          <ColorBoxContent variant='white'>
            <BreadcrumbsMUI
              crumbs={[
                {
                  route: '/app/',
                  name: 'Manage Inventory'
                },
                {
                  route: '/app/manage-sims',
                  name: 'Manage SIMs'
                },
                {
                  route: 'batch-update',
                  name: 'Batch Update'
                }
              ]}
            />
          </ColorBoxContent>
        </Grid>
        <Grid item>
          <ColorBoxContent variant='white'>
            <Stepper activeStep={activeStep} orientation='vertical'>
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={
                      index === steps.length - 1 ? (
                        <Typography variant='caption'>Last step</Typography>
                      ) : null
                    }
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    <Typography>{step.description}</Typography>
                    <Box> {step.component}</Box>
                    <Box sx={{ mb: 2 }}>
                      <div>
                        <Button
                          variant='contained'
                          onClick={step.handleNextStep}
                          sx={{ marginTop: 1, marginRight: 1 }}
                        >
                          {index === steps.length - 1 ? 'Submit' : 'Continue'}
                        </Button>
                        <Button
                          disabled={index === 0}
                          onClick={handlePrevStep}
                          sx={{ marginTop: 1, marginRight: 1 }}
                        >
                          Back
                        </Button>
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            {activeStep === steps.length && (
              <Paper square elevation={0} sx={{ p: 3 }}>
                <Typography>All steps completed!</Typography>
                <Button sx={{ marginTop: 1, marginRight: 1 }}>Back</Button>
              </Paper>
            )}
          </ColorBoxContent>
        </Grid>
        <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
          <DialogTitle>
            <Typography variant='h4'>{error ? 'Error' : 'Success'}</Typography>
          </DialogTitle>
          <DialogContent>
            <Typography>{error ? ERR_UNEXPECTED : dialogMsg}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant='filled' onClick={() => setIsDialogOpen(false)}>
              Done
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </PageLayout>
  )
}
